import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { Search, Plus, Briefcase, Loader2, ArrowUpRight } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  dealsApi,
  usersApi,
  type DealStatus,
  type PipelineStage,
  type DealListParams,
} from "@/lib/api"
import { formatCurrency } from "@shared/utils/currency"
import { formatDate, timeAgo } from "@shared/utils/date"

const STATUS_LABEL: Record<DealStatus, string> = {
  new_lead: "New Lead",
  awaiting_docs: "Awaiting Docs",
  docs_received: "Docs Received",
  approved: "Approved",
  settled: "Settled",
  closed: "Closed",
  lost: "Lost",
  on_hold: "On Hold",
}
const STATUS_VARIANT: Record<
  DealStatus,
  "info" | "warning" | "success" | "muted" | "destructive" | "default"
> = {
  new_lead: "info",
  awaiting_docs: "warning",
  docs_received: "info",
  approved: "success",
  settled: "success",
  closed: "muted",
  lost: "destructive",
  on_hold: "muted",
}

export default function DealsListPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get("q") ?? "")

  const params: DealListParams = {
    q: searchParams.get("q") ?? undefined,
    status: (searchParams.get("status") as DealStatus) || undefined,
    pipelineStage: (searchParams.get("pipelineStage") as PipelineStage) || undefined,
    ownerId: searchParams.get("ownerId") ?? undefined,
    page: Number(searchParams.get("page") ?? 1),
    pageSize: 50,
  }

  const deals = useQuery({
    queryKey: ["deals", params],
    queryFn: () => dealsApi.list(params),
  })
  const brokers = useQuery({
    queryKey: ["users-active"],
    queryFn: () => usersApi.list(),
  })

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete("page")
    setSearchParams(next)
  }

  const actions = (
    <div className="flex gap-2">
      <Button variant="outline" asChild>
        <Link to="/pipeline">Pipeline view</Link>
      </Button>
      <Button asChild>
        <Link to="/deals/new">
          <Plus className="h-4 w-4" />
          New Deal
        </Link>
      </Button>
    </div>
  )

  return (
    <AppShell title="Deals" subtitle="All deals across your pipeline" actions={actions}>
      <div className="space-y-4">
        <Card className="p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <form
              onSubmit={(e) => {
                e.preventDefault()
                updateFilter("q", searchInput.trim())
              }}
              className="relative flex-1"
            >
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder="Search by deal name, lender, notes…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </form>
            <select
              className="border-input bg-background h-10 rounded-md border px-3 text-sm"
              value={params.status ?? ""}
              onChange={(e) => updateFilter("status", e.target.value)}
            >
              <option value="">All statuses</option>
              {(Object.keys(STATUS_LABEL) as DealStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
            <select
              className="border-input bg-background h-10 rounded-md border px-3 text-sm"
              value={params.ownerId ?? ""}
              onChange={(e) => updateFilter("ownerId", e.target.value)}
            >
              <option value="">All brokers</option>
              {brokers.data?.users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </select>
          </div>
        </Card>

        {deals.isLoading ? (
          <Card className="grid place-items-center p-16">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </Card>
        ) : !deals.data?.items.length ? (
          <Card className="p-16 text-center">
            <div className="bg-rc-blue-50 text-rc-blue-600 mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full">
              <Briefcase className="h-7 w-7" />
            </div>
            <h3 className="text-foreground text-lg font-semibold">No deals yet</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              Create your first deal to start tracking.
            </p>
            <Button asChild className="mt-4">
              <Link to="/deals/new">
                <Plus className="h-4 w-4" />
                New Deal
              </Link>
            </Button>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-border bg-rc-grey-50 text-muted-foreground border-b text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Deal</th>
                  <th className="px-4 py-3 text-left font-medium">Contact</th>
                  <th className="px-4 py-3 text-right font-medium">Amount</th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">Owner</th>
                  <th className="px-4 py-3 text-left font-medium">Close Date</th>
                  <th className="px-4 py-3 text-left font-medium">Updated</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {deals.data.items.map((d) => (
                  <tr key={d.id} className="border-border hover:bg-rc-grey-50 border-b">
                    <td className="px-4 py-3">
                      <Link
                        to={`/deals/${d.id}`}
                        className="text-foreground hover:text-rc-blue-600 font-medium"
                      >
                        {d.dealName ?? "Untitled deal"}
                      </Link>
                      {d.lender && <div className="text-muted-foreground text-xs">{d.lender}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/contacts/${d.contactId}`}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {d.contactBusinessName ??
                          `${d.contactFirstName ?? ""} ${d.contactLastName ?? ""}`.trim() ??
                          "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">
                      {formatCurrency(d.naf ?? d.loanAmount ?? 0)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_VARIANT[d.status]}>{STATUS_LABEL[d.status]}</Badge>
                    </td>
                    <td className="text-muted-foreground px-4 py-3">
                      {d.ownerName ?? "Unassigned"}
                    </td>
                    <td className="text-muted-foreground px-4 py-3 text-xs">
                      {d.expectedCloseDate ? formatDate(d.expectedCloseDate) : "—"}
                    </td>
                    <td className="text-muted-foreground px-4 py-3 text-xs">
                      {timeAgo(d.updatedAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/deals/${d.id}`}
                        className="text-muted-foreground hover:text-foreground inline-flex"
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </AppShell>
  )
}
